import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {type CommitFailure} from './getCommitFailure';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

/**
 * User-facing alert for a commit failure that needs one. Shared by ordering a configuration and
 * by applying a configuration alternative, which answers with the same commit error codes.
 * `conflict`, `review-conflict` and transport errors are handled by the caller.
 */
export const getCommitFailureAlert = (failure: CommitFailure | undefined) =>
  match(failure?.kind)
    .with('already-ordered', () => ({
      variant: 'error' as const,
      message: t('entity.newVehicle.order.alreadyOrdered', 'This configuration is already ordered'),
    }))
    .with('different-business-case', () => ({
      variant: 'error' as const,
      message: t(
        'entity.newVehicle.order.differentBusinessCase',
        'This configuration is linked to a different Business Case'
      ),
    }))
    .with('business-case-already-selected', () => ({
      variant: 'error' as const,
      message: t(
        'entity.newVehicle.order.businessCaseAlreadySelected',
        'This Business Case already has a selected configuration or vehicle'
      ),
    }))
    .with('materialized-outside-customer-commitment', () => ({
      variant: 'error' as const,
      message: t(
        'entity.newVehicle.order.materializedOutsideCustomerCommitment',
        'This configuration was ordered outside the customer commitment'
      ),
    }))
    .with('business-case-state-not-allowed', () => ({
      variant: 'warning' as const,
      message: t(
        'entity.newVehicle.order.businessCaseStateNotAllowed',
        'Business Case must be in Concept or Offer'
      ),
    }))
    .with('configuration-not-selected', () => ({
      variant: 'error' as const,
      message: t(
        'entity.newVehicle.order.configurationNotSelected',
        'Another configuration was selected for this Business Case'
      ),
    }))
    .with('business-case-identity-mismatch', () => ({
      variant: 'error' as const,
      message: t(
        'entity.newVehicle.order.businessCaseIdentityMismatch',
        'Configuration branch or customer does not match the Business Case'
      ),
    }))
    .with('configuration-not-found', () => ({
      variant: 'error' as const,
      message: t('entity.newVehicle.order.configurationNotFound', 'Configuration not found'),
    }))
    .with('business-case-not-found', () => ({
      variant: 'error' as const,
      message: t('entity.newVehicle.order.businessCaseNotFound', 'Business Case not found'),
    }))
    .otherwise(() => undefined);
